import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { Search, FileSpreadsheet, RefreshCw, Filter, TrendingUp, TrendingDown, Calendar, Clock, Upload as UploadIcon } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { getFirms, listStockReports, getLatestStockReports, searchStockItems } from '../../services/taskApi';

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const FlowStockReport = () => {
  const { isDark, user } = useOutletContext();
  const canUpload = user?.role === 'admin' || user?.flow_role === 'store_user';
  const [firms, setFirms] = useState([]);
  const [reports, setReports] = useState([]);
  const [latestPerFirm, setLatestPerFirm] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterFirm, setFilterFirm] = useState('');
  const [filterReport, setFilterReport] = useState('');
  const [onlyInStock, setOnlyInStock] = useState(false);

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';

  const fetchAll = useCallback(async () => {
    try {
      const [f, r, lp] = await Promise.all([getFirms(), listStockReports(), getLatestStockReports()]);
      setFirms(f.data || []);
      setReports(r.data || []);
      setLatestPerFirm(lp.data || []);
    } catch { toast.error('Failed to load reports'); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (search.trim()) params.q = search.trim();
      if (filterFirm) params.firm_id = filterFirm;
      if (filterReport) params.report_id = filterReport;
      if (onlyInStock) params.only_in_stock = true;
      const res = await searchStockItems(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Search failed'); }
    finally { setLoading(false); }
  }, [search, filterFirm, filterReport, onlyInStock]);

  useEffect(() => {
    const t = setTimeout(doSearch, 250);
    return () => clearTimeout(t);
  }, [doSearch]);

  const firmCodeOf = useMemo(() => {
    const m = {};
    (latestPerFirm || []).forEach(r => { m[r.firm_id] = r.firm_code; });
    return m;
  }, [latestPerFirm]);

  return (
    <div className="space-y-5" data-testid="flow-stock-report">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Stock Report</h1>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Search the latest Tally inventory snapshot per firm.</p>
        </div>
        {canUpload && (
          <Link to="/tasks/flow/stock-upload">
            <Button className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="go-to-upload">
              <UploadIcon size={14} className="mr-1.5" /> Upload Stock
            </Button>
          </Link>
        )}
      </div>

      {/* Latest report per firm — shows last updated date/time prominently */}
      {latestPerFirm.length === 0 ? (
        <Card className={cardCls}>
          <CardContent className="p-6 text-center">
            <FileSpreadsheet size={32} className={`mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>No stock report uploaded yet.</p>
            {canUpload && <p className={`text-xs mt-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Use "Stock Upload" from the sidebar to add the first snapshot.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {latestPerFirm.map(r => {
            const ageInDays = (Date.now() - new Date(r.uploaded_at).getTime()) / 86400000;
            const isStale = ageInDays > 1.5;  // > 1.5 days old
            return (
              <Card key={r.id} className={cardCls} data-testid={`firm-summary-${r.firm_code}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-md text-[11px] font-bold ${isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                      <FileSpreadsheet size={12} /> {r.firm_code}
                    </div>
                    <span className={`text-[10px] inline-flex items-center gap-1 ${isStale ? 'text-amber-400' : (isDark ? 'text-green-400' : 'text-green-600')}`} title={new Date(r.uploaded_at).toLocaleString()}>
                      <Clock size={10} /> Last updated {timeAgo(r.uploaded_at)}
                    </span>
                  </div>
                  <p className={`mt-2 font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.firm_name}</p>
                  <div className={`mt-1 text-[10px] inline-flex items-center gap-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    <Calendar size={9} /> Report date: <b className={isDark ? 'text-white/70' : 'text-gray-700'}>{r.report_date}</b>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <p className={`text-[9px] uppercase tracking-wider ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Items</p>
                      <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.total_items.toLocaleString('en-IN')}</p>
                    </div>
                    <div>
                      <p className={`text-[9px] uppercase tracking-wider ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Total Qty</p>
                      <p className={`text-lg font-bold ${(r.total_qty || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{Math.round(r.total_qty).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                  <div className={`mt-2 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    By {r.uploaded_by_name || 'Admin'}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Search & Filters */}
      <Card className={cardCls}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              <Input className={`pl-9 h-10 ${inputCls}`} placeholder="Search by item name or brand group (e.g., M12, ADDISON, TAP)..."
                value={search} onChange={e => setSearch(e.target.value)} data-testid="stock-search" />
            </div>
            <select className={`h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={filterFirm} onChange={e => { setFilterFirm(e.target.value); setFilterReport(''); }} data-testid="filter-firm">
              <option value="">All firms</option>
              {firms.map(f => <option key={f.id} value={f.id}>{f.name}{firmCodeOf[f.id] ? ` (${firmCodeOf[f.id]})` : ''}</option>)}
            </select>
            <select className={`h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={filterReport} onChange={e => setFilterReport(e.target.value)} data-testid="filter-report">
              <option value="">Latest snapshot</option>
              {reports.filter(r => !filterFirm || r.firm_id === filterFirm).map(r => (
                <option key={r.id} value={r.id}>{r.firm_code} · {r.report_date} · {new Date(r.uploaded_at).toLocaleDateString()}</option>
              ))}
            </select>
            <label className={`flex items-center gap-2 px-3 h-10 rounded-lg border cursor-pointer text-xs whitespace-nowrap ${isDark ? 'border-white/10 text-white/70 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              <input type="checkbox" checked={onlyInStock} onChange={e => setOnlyInStock(e.target.checked)} data-testid="only-in-stock" />
              <Filter size={12} /> Only in-stock
            </label>
          </div>
          <div className={`mt-2 text-[11px] flex items-center justify-between ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            <span>Showing <b className={isDark ? 'text-white/70' : 'text-gray-700'}>{items.length}</b> of <b className={isDark ? 'text-white/70' : 'text-gray-700'}>{total.toLocaleString('en-IN')}</b> matching rows</span>
            <button onClick={doSearch} className="inline-flex items-center gap-1 hover:text-blue-400" data-testid="refresh-search">
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className={cardCls}>
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className={`p-10 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Loading…</div>
          ) : items.length === 0 ? (
            <div className={`p-10 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>No matching items. Try a different search.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}>
                  <tr className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    <th className="text-left py-3 px-4 font-medium w-16">Firm</th>
                    <th className="text-left py-3 px-4 font-medium">Item / Particulars</th>
                    <th className="text-left py-3 px-4 font-medium">Group</th>
                    <th className="text-right py-3 px-4 font-medium">Qty in Tally</th>
                    <th className="text-left py-3 px-4 font-medium">Report Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const inStock = it.quantity > 0;
                    return (
                      <tr key={it.id} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-100 hover:bg-gray-50'} transition-colors`} data-testid={`stock-row-${it.id}`}>
                        <td className="py-2.5 px-4">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold ${isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>{it.firm_code}</span>
                        </td>
                        <td className={`py-2.5 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="font-medium leading-snug">{it.particulars}</div>
                        </td>
                        <td className={`py-2.5 px-4 text-[11px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{it.group || '—'}</td>
                        <td className={`py-2.5 px-4 text-right font-bold tabular-nums ${inStock ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>
                          <span className="inline-flex items-center gap-1">
                            {inStock ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {Math.round(it.quantity).toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className={`py-2.5 px-4 text-[11px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{it.report_date}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {total > items.length && (
                <div className={`p-3 text-center text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  Showing first {items.length} rows. Refine your search to narrow down further.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FlowStockReport;
